import { message, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
  getDiscordTriggers,
  getTriggerExecutions,
} from "../../lib/discordQueries";
import {
  DiscordTrigger,
  DiscordTriggersRpc,
  QuestionColumn,
  ResponseAggregatesRpc,
  TriggerExecutionsRpc,
  TriggerQuestionResponse,
} from "../../lib/discordTypes";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import moment from "moment";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

/**
 * Maps an array into a antd-compatible list of filters
 * @param items array of items
 * @returns antd table compatible filters
 */
const makeFilters = (items: string[]) =>
  items.map((item) => {
    return { text: item, value: item };
  });

type Response = {
  type?: string;
  userId?: string;
  content?: string;
  platform?: string;
  channelId?: string;
  messageId?: string;
  communityId?: string;
  stepId?: string;
  created_at?: string;
  key?: number;
};

function useExistingTriggers(guildId: string, workspace: string) {
  const result = useQuery<DiscordTriggersRpc[]>(
    ["discord-triggers", { guildId, workspace }],
    async () => {
      const triggers = await getDiscordTriggers(guildId, workspace);
      return triggers;
    }
  );
  return result;
}

async function fetchTriggerQuestionsAndResponses(questionRequest: {
  rootTriggerId: string;
  startDate: string;
  endDate: string;
}) {
  const { rootTriggerId, startDate, endDate } = questionRequest;

  //Get current date and a date one week ago
  try {
    const query = getSupaClient()
      .from("trigger_responses")
      .select()
      .eq("root_trigger_id", rootTriggerId)
      .neq("answer", null)
      .order("created_at", { ascending: false });

    if (startDate) {
      query.gte("created_at", startDate);
    }
    if (endDate) {
      query.lte("created_at", endDate);
    }
    const { data, error } = await query;
    if (error) {
      console.log("error in triggerResponses", error);
      return null;
    } else {
      console.log({
        _root_trigger_id: rootTriggerId,
        start_time: startDate,
        end_time: endDate,
      });

      const triggerQuestions = await mapTriggerQuestionAndResponses(data || []);
      return triggerQuestions;
    }
  } finally {
  }
}

function mapTriggerQuestionAndResponses(
  triggerQuestionAndResponses: TriggerQuestionResponse[]
) {
  const questions: {
    question: string;
    stepId: string;
    createdAt: string;
    taskType: string;
  }[] = [];
  // For every send_multiple_select or send_survey within a trigger we group
  // all their replies, and we'll use them as a filter value later on .
  const surveys: { stepId: string; replies: string[] }[] = [];
  let stringArrayFilters = "";
  for (const triggerRow of triggerQuestionAndResponses) {
    const { question, _step_id: stepId, created_at, task_type } = triggerRow;
    //Check if the incoming question already exist in questions array. The way how we would know
    //If the question already exist or not is checking if the question step id exist in questions array.
    if (!questions.map(({ stepId }) => stepId).includes(stepId)) {
      if (["send_survey", "send_multiple_select"].includes(task_type))
        surveys.push({ stepId, replies: [] });
      //TODO: use a regex to remove all markdown syntax in the future.
      questions.push({
        question: question.replaceAll("**", ""),
        stepId,
        createdAt: created_at!,
        taskType: task_type,
      });
    }
  }
  //After getting all the questions we map them into an object that can be consumed by antd-table
  // maybe this mapping should happen inside the component which will consume this data rather than here.

  const mapQuestions: QuestionColumn[] = questions.map(
    ({ question, stepId, createdAt, taskType }, index) => {
      function truncate(string: string, number: number) {
        if (string.length <= number) {
          return string;
        }
        return string.slice(0, number) + "...";
      }
      const title = (
        <Tooltip placement="bottom" title={question}>
          <span>{truncate(question, 30)}</span>
        </Tooltip>
      );
      return {
        title,
        dataIndex: `reply${index}`,
        key: `reply${index}`,
        stepId,
        createdAt,
        rowKey: index,
        taskType,
      };
    }
  );

  //Add static fields
  mapQuestions.unshift({
    title: "Execution Date",
    dataIndex: `executionDate`,
    key: `executionDate`,
    stepId: "",
    createdAt: "",
    rowKey: mapQuestions.length,
    fixed: "left",
    width: 150,
    sorter: (a: any, b: any) => {
      return (
        dayjs(a.executionDate, "YYYY-MM-DDTHH:mm:ss.sssZ").valueOf() -
        dayjs(b.executionDate, "YYYY-MM-DDTHH:mm:ss.sssZ").valueOf()
      );
    },
    render: (text: string) => {
      return dayjs(text).format("MMM D H:mm a");
    },
  });

  mapQuestions.unshift({
    title: "Username",
    dataIndex: `username`,
    key: `username`,
    stepId: "",
    createdAt: "",
    rowKey: mapQuestions.length,
    fixed: "left",
    width: 200,
    sorter: (a: any, b: any) => a.username.localeCompare(b.username),
  });

  const executions: any[] = [];

  // Iterate over all the raw questions to find their root execution ids, for example
  // if we have 10 questions which have the same root_id, we would end up with an object with 10 steps
  triggerQuestionAndResponses.forEach((step) => {
    let rootExecution = executions.find(
      (execution) => execution.rootId == step.root_execution_id
    );

    //If the root id of the incoming question does not exist we create a new root execution object.
    if (!rootExecution) {
      rootExecution = {};
      rootExecution.rootId = step.root_execution_id;
      rootExecution.username = `${step.username}${
        step.discriminator ? `#${step.discriminator}` : ""
      }`;
      rootExecution.createdAt = step.created_at;
      rootExecution.steps = [];
      executions.push(rootExecution);
    }
    //Add all the steps to its root execution.
    rootExecution.steps = [...rootExecution.steps, step];
  });

  const userResponses = executions.map((rootExecution, index) => {
    const entry: any = {};

    // For every step within the execution we find its question.
    rootExecution.steps.forEach((step: any) => {
      const surveyStep = surveys.find((survey) => {
        return survey.stepId === step._step_id;
      });

      const question = mapQuestions.find((mapQuestion) => {
        return mapQuestion.stepId === step._step_id;
      });

      if (!question) {
        throw new Error(
          `Step ${step._step_id} does not found in mapQuestion list`
        );
      }

      // We check if the step is a survey step (send_survey or send_multiple_select task type).
      // If it is we add the survey answer to the entry.
      // This is going to be used as filter values when the table is filtered.
      if (surveyStep) {
        /**
         * check if the answer is in array format, this is done by checking
         * if the first and last characters are square brackets.
         * This array format comes from a send_multiple_select task type
         * for send_survey the answer is a string format.
         */

        if (
          step.answer[0] === "[" &&
          step.answer.slice(step.answer.length - 1) === "]"
        ) {
          // If the answer is an array format, we split it and push every value into the array.
          entry[question.key] = [];
          step.answer
            .match(/[A-Za-z0-9,]/gm)
            .join("")
            .split(",")
            .forEach((answer: string) => {
              if (!surveyStep.replies.includes(answer)) {
                surveyStep.replies.push(answer);
              }
              entry[question.key] = [...entry[question.key], answer];
            });
          entry[question.key] = entry[question.key].join(", ");
        } else {
          if (!surveyStep.replies.includes(step.answer)) {
            surveyStep.replies.push(step.answer);
          }
          entry[question.key] = step.answer;
        }
      } else {
        entry[question.key] = step.answer;
      }
    });
    entry.rootExecutionId = rootExecution.rootId;
    entry.rowKey = index;
    entry.username = rootExecution.username;
    entry.executionDate = moment(rootExecution.createdAt).format(
      "YYYY-MM-DDTHH:mm:ss.sssZ"
    );
    return entry;
  });
  for (let questionId in mapQuestions) {
    const question = mapQuestions[questionId];
    const surveyStep = surveys.find((survey) => {
      return survey.stepId === question.stepId;
    });
    if (surveyStep) {
      mapQuestions[questionId] = {
        ...question,
        filteredValue: null,
        filters: makeFilters(surveyStep.replies),
        onFilter: (value: string, record: any) => {
          return record[question.key]
            ? record[question.key].includes(value)
            : false;
        },
      };
    }
  }

  return {
    columns: mapQuestions,
    userResponses,
  };
}

/**
 *
 * @returns
 * execTriggers - a list of triggers
 * triggerExecutions - a list of trigger executions
 * triggerResponses - a list of responses for a given trigger execution
 * loadingExec - wether the trigger executions are loading
 * setLoadingExec - state updating function fro loadingExec
 * loadDiscordTriggers - callback that loads the triggers for a given guild including execution count
 * loadTriggerExecutions - callback that loads the trigger executions for a given trigger
 * changeTriggerVisibility - callback to change the visibility of a given trigger
 * deleteTrigger - callback to delete a given trigger
 * loadResponses - callback to load a list of responses for a given trigger execution
 * loadTriggers - callback to load the triggers for the current guild
 */
function useDiscordTriggers() {
  const guildId = localStorage.getItem("guild-id") || "";
  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);

  const [teamTriggers, setTeamTriggers] = useState<any>();
  const { data: triggers, isLoading: loadingExistingTriggers } =
    useExistingTriggers(guildId, workspace);
  const queryClient = useQueryClient();
  const [loadingExec, setLoadingExec] = useState<boolean>(false);
  const [triggerExecutions, setTriggerExecutions] = useState<
    TriggerExecutionsRpc[]
  >([]);
  const [triggerResponses, setTriggerResponses] = useState<Response[]>([]);
  const [triggerQuestions, setTriggerQuestions] = useState<any>({
    columns: [],
    userResponses: [],
  });
  const [execTriggers, setExecTriggers] = useState<DiscordTriggersRpc[] | null>(
    null
  );
  const [responseAggregates, setResponseAggregates] = useState<
    ResponseAggregatesRpc[] | null
  >(null);

  const [loadingResponseAggregates, setLoadingResponseAggregates] =
    useState<Boolean>(false);

  useEffect(() => {
    setExecTriggers(triggers as DiscordTriggersRpc[]);
  }, [triggers]);

  /**
   * Loads triggers for the current guild
   */
  const loadTriggers = async () => {
    const { data, error } = await supabase
      .from("triggers")
      .select("*")
      .eq("community_id", guildId)
      .order("id", { ascending: false });
    if (error) {
      message.error("Try again");
    }
    if (data) {
      setTeamTriggers(data);
    }
  };

  const loadExecTriggers = async () => {
    setExecTriggers(null);
    const triggers = await getDiscordTriggers(guildId, workspace);
    setExecTriggers(triggers);
  };

  /**
   * Loads triggers for the current guild, including execution count
   */
  const loadDiscordTriggers = async () => {
    queryClient.invalidateQueries(["discord-triggers", { guildId, workspace }]);
  };

  const loadTriggerExecutions = async (
    triggerId: string,
    communityId: string,
    workspaceId: string,
    startDate?: string,
    endDate?: string
  ) => {
    setLoadingExec(true);
    const executions = await getTriggerExecutions(
      triggerId,
      communityId,
      workspaceId,
      startDate,
      endDate
    );
    setTriggerExecutions(executions);
    setLoadingExec(false);
  };

  /**
   * Toggle visibility of trigger
   * @param trigger object of selected trigger
   */
  const changeTriggerVisibility = async (trigger: DiscordTriggersRpc) => {
    const { data, error } = await supabase
      .from("triggers")
      .update({ is_active: !trigger.is_active })
      .match({ id: trigger.trigger_id });
    window.analytics.track(
      `Trigger - trigger turned ${!trigger.is_active === true ? "on" : "off"}`
    );
    if (error) {
      message.error("Error updating");
    }
    if (data) {
      await loadDiscordTriggers();
      message.success("Successfully updated");
    }
  };

  /**
   * Delete selected trigger
   * @param trigger object of selected trigger
   */
  const deleteTrigger = async (trigger: DiscordTriggersRpc) => {
    const { data, error } = await supabase
      .from("triggers")
      .delete()
      .match({ community_id: guildId, id: trigger.trigger_id });
    if (error) {
      message.error("Error deleting");
    }
    if (data) {
      message.success("Successfully deleted");
      window.analytics.track(`Trigger - deleted`);
    }
    loadDiscordTriggers();
  };

  /**
   * Delete selected trigger by setting is_archived to true
   * @param trigger object of selected trigger
   */
  const archiveTrigger = async (trigger: DiscordTriggersRpc) => {
    const path = `/triggers/${trigger.trigger_id}`;
    const fullURL = `${process.env["REACT_APP_API_URL"]}${path}`;
    const authToken = supabase.auth.session()?.access_token;
    const response = await fetch(fullURL, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (response.ok) {
      await loadDiscordTriggers();
      message.success("Successfully deleted");
      window.analytics.track(`Trigger - deleted`);
    } else {
      let reason = await response.text();
      message.error("Error deleting");
      throw new Error(reason);
    }
  };

  /**
   * Loads the responses for a given trigger
   * @param trigger the trigger object
   */
  const loadResponses = async (trigger: any) => {
    const stepIds = [];
    // eslint-disable-next-line
    const responses: Response[] = [];
    // eslint-disable-next-line
    for (let element in trigger.variables) {
      // Loop through the variables object and select only the nested objects that
      // have a "response" porperty
      let responseObj = {};
      // eslint-disable-next-line
      if (trigger.variables[element].hasOwnProperty("response")) {
        responseObj = {
          ...trigger.variables[element].response,
          stepId: element,
        };
        responses.push(responseObj);
        stepIds.push(element);
      }
    }
    // In order to get the "created_at" value for each response, we have to
    // fetch the corresponding steps
    const { data, error } = await supabase
      .from("trigger_steps")
      .select("*")
      .in("id", stepIds)
      .filter("trigger_id", "eq", trigger.trigger_id);

    if (error) {
      console.log("Error at loadResponses:", error);
    }
    // Attach the correspondent "created_at" value to each response
    // We take this value from a step object
    const parsedResponses: Response[] = [];
    if (data) {
      responses.forEach((response) => {
        data.forEach((step) => {
          if (step.id === response.stepId) {
            const responseObj = {
              ...response,
              created_at: step.created_at,
              key: step.id,
            };
            parsedResponses.push(responseObj);
          }
        });
      });
    }
    setTriggerResponses(parsedResponses);
  };
  /**
   * Load question and  responses for a given trigger
   * @param trigger the trigger object
   */
  const loadTriggerQuestionsAndResponses = async (questionRequest: {
    rootTriggerId: string;
    startDate: string;
    endDate: string;
  }) => {
    setLoadingExec(true);
    const triggerQuestions = await fetchTriggerQuestionsAndResponses(
      questionRequest
    );
    setTriggerQuestions(triggerQuestions || []);
    setLoadingExec(false);
  };

  /**
   * Load trigger response aggregates
   * @param rootTriggerId
   * @returns
   */
  const loadResponseAggregates = async (
    rootTriggerId: string,
    startDate: string,
    endDate: string
  ): Promise<ResponseAggregatesRpc[] | null> => {
    setLoadingResponseAggregates(true);
    const { data, error } = await supabase.rpc("response_aggregates", {
      _root_trigger_id: rootTriggerId,
      start_time: startDate,
      end_time: endDate,
    });
    setLoadingResponseAggregates(false);
    if (error) {
      console.log("error in loadResponseAggregates", error);
      return null;
    } else {
      console.log({
        _root_trigger_id: rootTriggerId,
        start_time: startDate,
        end_time: endDate,
      });

      console.log("data in loadResponseAggregates", data);
      setResponseAggregates(data);
      return data;
    }
  };

  // Get trigger by id without passing workspace nor community, this method is used for admins.
  const getTriggerById = async (triggerId: string) => {
    const { data, error } = await supabase
      .from<DiscordTrigger>("triggers")
      .select("id, workspace")
      .eq("id", triggerId);
    if (error) {
      message.error("Error getting trigger");
    }
    return data;
  };

  return {
    execTriggers,
    triggerExecutions,
    triggerResponses,
    triggerQuestions,
    loadingExec,
    responseAggregates,
    setLoadingExec,
    setTriggerQuestions,
    setTriggerExecutions,
    loadTriggerExecutions,
    changeTriggerVisibility,
    deleteTrigger,
    archiveTrigger,
    loadResponses,
    loadTriggers,
    setTriggerResponses,
    loadTriggerQuestionsAndResponses,
    loadExecTriggers,
    getTriggerById,
    loadResponseAggregates,
    loadingResponseAggregates,
  };
}
export default useDiscordTriggers;

export { fetchTriggerQuestionsAndResponses };
